import { writable, derived } from "svelte/store";
import { browser } from "$app/environment";
import { authStore } from "./authStore";
import type { Readable } from "svelte/store";
import type { CampaignMetadata, GroupedCampaigns, User } from "$lib/types";

class CampaignHistoryManager {
  public static readonly STORAGE_KEY = "campaignHistory";
  public static readonly ANONYMOUS_USER = "anonymous";

  static getUserIdentifier(user: User | undefined): string {
    if (!user) return this.ANONYMOUS_USER;
    return user.id || user.email || this.ANONYMOUS_USER;
  }

  static mergeCampaigns(existing: CampaignMetadata[], incoming: CampaignMetadata[]): CampaignMetadata[] {
    const campaignMap = new Map<string, CampaignMetadata>();

    incoming.forEach((campaign) => {
      campaignMap.set(campaign.slug, campaign);
    });
    existing.forEach((campaign) => {
      campaignMap.set(campaign.slug, campaign);
    });

    return Array.from(campaignMap.values()).sort(
      (a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime(),
    );
  }

  // static loadStoredHistory(): CampaignMetadata[] {
  //   if (!browser) return [];

  //   try {
  //     const storedHistory = localStorage.getItem(this.STORAGE_KEY);
  //     if (!storedHistory) return [];

  //     const parsedHistory = JSON.parse(storedHistory);
  //     return this.attachUserIdToHistory(parsedHistory);
  //   } catch (error) {
  //     console.error("Error loading campaign history:", error);
  //     return [];
  //   }
  // }

  private static attachUserIdToHistory(history: CampaignMetadata[]): CampaignMetadata[] {
    const currentUser = authStore.getUser();
    const userID = this.getUserIdentifier(currentUser);

    return history.map((campaign) => (campaign.userID ? campaign : { ...campaign, userID }));
  }

  static saveToLocalStorage(campaigns: CampaignMetadata[]): void {
    if (!browser) return;

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(campaigns));
    } catch (error) {
      console.error("Error saving campaign history:", error);
    }
  }

  static getTimeGroupLabel(date: Date): string {
    if (isNaN(date.getTime())) return "Other";

    const timeFrames = this.getTimeFrames();

    if (date >= timeFrames.today) return "Today";
    if (date >= timeFrames.yesterday) return "Yesterday";
    if (date >= timeFrames.lastWeek) return "Last week";
    if (date >= timeFrames.lastMonth) return "Last month";

    return date.toLocaleString("default", { month: "long", year: "numeric" });
  }

  private static getTimeFrames() {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    return { today, yesterday, lastWeek, lastMonth };
  }
}

// Create the base store
const { subscribe, update } = writable<CampaignMetadata[]>();

// Create and export the main store
export const campaignHistoryStore = {
  subscribe,

  addCampaign(campaign: CampaignMetadata) {
    update((campaigns) => {
      const userID = CampaignHistoryManager.getUserIdentifier(authStore.getUser());

      const newCampaign = {
        ...campaign,
        createdAt: new Date(),
        updatedAt: new Date(),
        userID,
      } as CampaignMetadata;

      const updatedCampaigns = [newCampaign, ...campaigns.filter((c) => c.slug !== campaign.slug)];

      CampaignHistoryManager.saveToLocalStorage(updatedCampaigns);
      return updatedCampaigns;
    });
  },

  updateCampaign(slug: string, updates: Partial<CampaignMetadata>) {
    update((campaigns) => {
      const userID = CampaignHistoryManager.getUserIdentifier(authStore.getUser());

      const updatedCampaigns = campaigns.map((campaign) =>
        campaign.slug === slug && (campaign.userID === userID || !campaign.userID)
          ? { ...campaign, ...updates, updatedAt: new Date(), userID }
          : campaign,
      );

      CampaignHistoryManager.saveToLocalStorage(updatedCampaigns);
      return updatedCampaigns;
    });
  },

  removeCampaign(slug: string) {
    update((campaigns) => {
      if (!authStore.getUser()) return campaigns;

      const updatedCampaigns = campaigns.filter((c) => c.slug !== slug);
      CampaignHistoryManager.saveToLocalStorage(updatedCampaigns);
      return updatedCampaigns;
    });
  },

  clearHistory() {
    update((campaigns) => {
      const userID = CampaignHistoryManager.getUserIdentifier(authStore.getUser());
      const updatedCampaigns = campaigns.filter((campaign) => campaign.userID !== userID);

      CampaignHistoryManager.saveToLocalStorage(updatedCampaigns);
      return updatedCampaigns;
    });
  },

  setCampaigns(campaigns: CampaignMetadata[]) {
    update((existingCampaigns) => {
      const userID = CampaignHistoryManager.getUserIdentifier(authStore.getUser());

      const otherUserCampaigns = existingCampaigns.filter((campaign) => campaign.userID !== userID);

      const newCampaigns = campaigns.map((campaign) => ({
        ...campaign,
        userID,
      }));

      const updatedCampaigns = [...newCampaigns, ...otherUserCampaigns];
      CampaignHistoryManager.saveToLocalStorage(updatedCampaigns);
      return updatedCampaigns;
    });
  },

  appendCampaigns(newCampaigns: CampaignMetadata[]) {
    update((existingCampaigns) => {
      const userID = CampaignHistoryManager.getUserIdentifier(authStore.getUser());

      const processedNewCampaigns = newCampaigns.map((campaign) => ({
        ...campaign,
        userID,
      }));

      const mergedCampaigns = CampaignHistoryManager.mergeCampaigns(existingCampaigns, processedNewCampaigns);

      CampaignHistoryManager.saveToLocalStorage(mergedCampaigns);
      return mergedCampaigns;
    });
  },

  getCampaignsForCurrentUser(): CampaignMetadata[] {
    const userID = CampaignHistoryManager.getUserIdentifier(authStore.getUser());
    let campaigns: CampaignMetadata[] = [];

    const unsubscribe = subscribe((currentCampaigns) => {
      campaigns = currentCampaigns.filter((campaign) => campaign.userID === userID);
    });

    unsubscribe();

    return campaigns;
  },

  getLatestCampaign(): CampaignMetadata | undefined {
    const userID = CampaignHistoryManager.getUserIdentifier(authStore.getUser());
    let latestCampaign: CampaignMetadata | undefined;

    const unsubscribe = subscribe((currentCampaigns) => {
      const userCampaigns = currentCampaigns.filter((campaign) => campaign.userID === userID);
      latestCampaign = userCampaigns.length > 0 ? userCampaigns[0] : undefined;
    });

    unsubscribe();

    return latestCampaign;
  },
};
